import { Formik, Form } from "formik";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { Grid, Icon } from "@mui/material";
import styled from "@emotion/styled";

import { useMutation, useQuery } from "@tanstack/react-query";

import MDTypography from "components/MDTypography";
import { useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { StripedDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { Error_, LoadingDialog } from "ui-components/Alert";
import dayjs from "dayjs";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import { downloadFile } from "layouts/views/common/file";
import { getFileName } from "layouts/views/common/file";
import { getDivisionGroupOptions } from "services/data.internal.service";
import { SelectField } from "layouts/views/components/Form";
import { inventoryColumns } from "./components/columns";
import { DivisionTransferCaption, StockTransferCaption } from "../components/Caption";
import { InboundSubmitButton } from "ui-components/Form";
import { Success } from "ui-components/Alert";
import { Error } from "ui-components/Alert";
import { Submitting } from "ui-components/Alert";
import { TextField } from "layouts/views/components/Form";
import { uploadDivisionTransfer } from "services/data.inventory.service";
import { createDivisionTransfer } from "services/data.inventory.service";
import { downloadDivisionTransferTemplate } from "services/data.inventory.service";
function deepCopy(obj) {
  if (obj === null || typeof obj !== "object") {
    // If the object is a primitive or null, return it as is
    return obj;
  }

  if (obj instanceof Date) {
    // If the object is a Date, create a new Date with the same value
    return new Date(obj.getTime());
  }

  if (Array.isArray(obj)) {
    // If the object is an array, create a new array and deep copy its elements
    return obj.map((element) => deepCopy(element));
  }

  // If the object is a regular object, create a new object and deep copy its properties
  const copiedObject = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      copiedObject[key] = deepCopy(obj[key]);
    }
  }

  return copiedObject;
}

const EditableStyledDataGrid = styled(StripedDataGrid)({
  ".MuiDataGrid-columnHeaderTitle": {
    whiteSpace: "normal !important",
    lineHeight: 1,
  },
  ".MuiDataGrid-columnHeader": {
    backgroundColor: "#f0f2f5",
  },
  ".MuiDataGrid-cell": {
    whiteSpace: "normal !important",
    "word-wrap": "break-word !important",
  },
  ".right-align-header": {
    textAlign: "right",
  },
});

function UploadDivisionTransfer() {
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [uploadResps, setUploadResps] = useState([]);
  const [fileResp, setFileResp] = useState([]);
  const uploadDivisionTransferMutation = useMutation(uploadDivisionTransfer, {});
  const uploadStatus = uploadDivisionTransferMutation.status === "loading";
  const [divisionGroupOpt, setDivisionGroupOpt] = useState([]);
  const [selectedDivisionGroup, setSelectedDivisionGroup] = useState("");
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const [createErrorResps, setCreateErrorResps] = useState([]);
  const [reasonCode, setReasonCode] = useState("");

  const handleSubmit = (values, actions) => {
    let formData = new FormData();
    formData.append("files", values.files[0], values.files[0].name);
    formData.append("customerCode", customerCode);
    formData.append("divisionGroup", values.divisionGroup);
    uploadDivisionTransferMutation.mutate(formData, {
      onSuccess: (data) => {
        setFileResp(data.data.data.data)
        setUploadResps(data.data.data.uploadResponses);
      },
      onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
    });
    actions.setSubmitting(false);
  };

  useQuery({
    queryKey: ["division-group-options", { customerCode }],
    queryFn: () => getDivisionGroupOptions({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      const options = data.data.data.map((v) => v);
      setDivisionGroupOpt(options);
    }
  })

  const exportMutation = useMutation(downloadDivisionTransferTemplate, {
    onSuccess: (response) => {
      downloadFile(response.data, `${getFileName("Division Template", customerCode)}.xlsx`);
    },
    onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
  });

  const handleDownload = () => {
    exportMutation.mutate({ customerCode, divisionGroup: selectedDivisionGroup })
  };

  const previewColumn = deepCopy(inventoryColumns).filter(x => x.field !== "actions").map((x) => {
    if (x.field === "toExpiryDate") {
      x.type = null;
      x.renderCell = (params) => {
        if (params.value !== null) {
          const date = new dayjs(params.value, 'DD/MM/YYYY')
          const formattedDate = date.format("DD / MM / YYYY");
          return <span>{formattedDate}</span>;
        }
      };
    }
    return x;
  });

  const createDivisionTransferMutation = useMutation(createDivisionTransfer);
  const [isInterDivisionSubmitting, setIsInterDivisionSubmitting] = useState(false);

  const onSubmitInterDivision = async () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    if (!Boolean(reasonCode)) {
      setAlert(<Error_ content={'Reason Code is required'} onChange={setAlert} />)
      return;
    }
    else if (reasonCode.length > 20) {
      setAlert(<Error_ content={'Reason Code must be less than 20 characters'} onChange={setAlert} />)
      return;
    }
    if (fileResp.some(file => (file.rows ?? []).filter(x => x.transferQuantity > 0).length <= 0)){
      setAlert(<Error_ content={'No data transferred'} onChange={setAlert} />)
      return;
    }

    const failedFiles = [];
    const successMsg = [];
    const errResp = [];
    setIsInterDivisionSubmitting(true);
    for (const file of fileResp) {
      const request = {
        stockTransferNo: null,
        customerCode: customerCode,
        reasonCode: reasonCode.slice(0, 20) ?? "",
        stockTransferDetails: (file.rows ?? []).filter(x => x.transferQuantity > 0).map(x => {
          const { expiryDate, toExpiryDate, ...data } = x;
          return {
            expiryDate: expiryDate ? dayjs(expiryDate, 'DD/MM/YYYY').format("YYYY-MM-DD") : null,
            toExpiryDate: toExpiryDate ? dayjs(toExpiryDate, 'DD/MM/YYYY').format("YYYY-MM-DD") : null,
            ...data,
          };
        })
      }
      try {
        await createDivisionTransferMutation.mutateAsync(request, {
          onSuccess: (data) => {
            successMsg.push(data.data.message)
          },
        });
      } catch (error) {
        console.log("error.response:", error.response);
  console.log("errRep:", error?.response?.data?.data);
        const errRep = error.response.data.data;
        errResp.push([...errRep])
        failedFiles.push(file.fileName);
      }
      if (Boolean(errResp))
        setCreateErrorResps(errResp)
      else
        setCreateErrorResps([])

    }

    // uploadMutation.mutate remaining files
    if (failedFiles.length) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      const errorMsg = 'Some Error Occurs, re-submitting upload request...'
      setAlert(<Error content={errorMsg} onChange={setAlert} />);
      let formData = new FormData();
      const input = document.getElementById("files");
      const files = Array.from(input.files).filter(f =>
        failedFiles.includes(f.name)
      );
      formData.append("files", files[0], files[0].name);
      formData.append("customerCode", customerCode);
      formData.append("divisionGroup", selectedDivisionGroup);
      uploadDivisionTransferMutation.mutate(formData, {
        onSuccess: (data) => {
          setFileResp(data.data.data.data)
          setUploadResps(data.data.data.uploadResponses);
          setIsInterDivisionSubmitting(false);
        },
        onError: (data) => setAlert(<Error_ data={data} onChange={setAlert} />),
      });
      return;
    }

    setIsInterDivisionSubmitting(false);
    setFileResp([]);
    setUploadResps([]);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setAlert(<Success content={successMsg.join('; ')} onChange={setAlert} />);
  }

  return (
    <InboundLayout lg={12}>
      {alert}
      {isDownloading && <ExportDialog />}
      {uploadStatus && <LoadingDialog />}
      {isInterDivisionSubmitting && <Submitting />}
      <InboundHeaderCard>
        <MDBox>
          <MDTypography color="info" variant="h3">
            Upload Division Transfer
          </MDTypography>
          <MDTypography color="dark" variant="h3">
            Selected Customer: {customerCode}
          </MDTypography>
          <MDButton variant="gradient" color="info" onClick={handleDownload} disabled={!Boolean(selectedDivisionGroup)}>
            Download Template
          </MDButton>

          <Formik initialValues={{ files: [], divisionGroup: selectedDivisionGroup }} onSubmit={handleSubmit}>
            {({ values, isSubmitting, setFieldValue }) => (
              <Form style={{ paddingTop: "20px" }}>
                <Grid item xs={6}>
                  <SelectField
                    field={{
                      type: "select",
                      id: "divisionGroup",
                      name: "divisionGroup",
                      label: "Division Group",
                      required: true,
                      value: selectedDivisionGroup,
                    }}
                    options={divisionGroupOpt}
                    onChange={(e, option) => {
                      setSelectedDivisionGroup(option)
                      setFieldValue("divisionGroup", option)
                    }}
                  />
                </Grid>
                <MDBox mt={2}>
                  <input
                    type="file"
                    name="files"
                    id="files"
                    onChange={(e) => {
                      setFieldValue("files", e.target.files);
                    }}
                  />
                </MDBox>
                <MDBox mt={2} display="flex" justifyContent="center">
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={values.files.length == 0 || values.divisionGroup == null || uploadStatus}
                  >
                    {uploadStatus ? "Uploading" : "Upload"}
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </InboundHeaderCard>

      {Boolean(uploadResps) && uploadResps.length > 0 && (
        <InboundHeaderCard>
          <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
            Upload Response{" "}
            <MDButton
              type="submit" variant="gradient" color="info"
              onClick={() => {
                const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_UploadInterDivision`;
                const headers = [
                  {
                    label: "Message",
                    key: "message",
                  },
                ]
                downloadCsv(filename, uploadResps.filter((x) => !x.success), headers)
              }}
            >
              <Icon>download</Icon>&nbsp;
              export
            </MDButton>
          </MDTypography>
          <StripedDataGrid
            columns={[
              {
                headerName: "Status",
                field: "success",
                align: "center",
                renderCell: (cell) =>
                  cell.row.success ? (
                    <StatusIcon color="success" icon="done" />
                  ) : (
                    <StatusIcon color="error" icon="close" />
                  ),
              },
              {
                headerName: "Message",
                field: "message",
                flex: 1,
                wordWrapEnabled: true,
                renderCell: (cell) => (
                  <div>
                    {(cell.row.message ?? []).map((c, i) => (
                      <p key={i}>{c}</p>
                    ))}
                  </div>
                ),
              },
            ]}
            rows={uploadResps}
            getRowHeight={(cell) => {
              if (cell.model?.message?.length >= 2) {
                return "auto";
              } else {
                return 50;
              }
            }}
          />
        </InboundHeaderCard>
      )}

      {Boolean(createErrorResps) && createErrorResps.length > 0 && (
        <InboundHeaderCard>
          <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
            Submit Error Response{" "}
            <MDButton
              type="submit" variant="gradient" color="info"
              onClick={() => {
                const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_UploadInterDivision_Submit`;
                const headers = [
                  { label: "Customer Code", key: "customerCode" },
                  { label: "SKU Code", key: "skuCode" },
                  { label: "Message", key: "message" },
                ]
                downloadCsv(filename, createErrorResps, headers)
              }}
            >
              <Icon>download</Icon>&nbsp;
              export
            </MDButton>
          </MDTypography>
          <StripedDataGrid
            columns={[
              { headerName: "Customer Code", field: "customerCode", flex: 1 },
              { headerName: "SKU Code", field: "skuCode", flex: 1 },
              { headerName: "Message", field: "message", flex: 1 },
            ]}
            rows={createErrorResps[0].map((x, index) => ({
              ...x,
              id: x.id ?? index
            }))}
          />
        </InboundHeaderCard>
      )}

      {fileResp.length > 0 && fileResp.map(file => (
        <InboundHeaderCard>
          <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
            {file.fileName}
          </MDTypography>

          {/* successful Row */}
          <div style={{ height: 500, width: "100%" }}>
            <EditableStyledDataGrid
              autoHeight={false}
              columns={previewColumn}
              rows={file.rows ?? []}
              sx={{
                borderRadius: "0.75rem",
              }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              getRowHeight={() => "auto"}
              hideFooterSelectedRowCount={true}
              slots={{
                footer: () => {
                  const totalQuantity =
                    file.rows && file.rows.length > 0
                      ? file.rows.reduce((total, det) => {
                        return total + det.transferQuantity;
                      }, 0)
                      : 0;
                  return <DivisionTransferCaption totalRows={file.rows ? file.rows.filter(x => x.transferQuantity > 0).length : 0} totalQuantity={totalQuantity} />;
                },
              }}
            />
          </div>

          {/* SKU not found errors */}
          {file.uploadResponses && file.uploadResponses.length > 0 && (
            <>
              <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
                Upload Response{" "}
                <MDButton
                  type="submit" variant="gradient" color="info"
                  onClick={() => {
                    const filename = `${file.fileName.replace(/\.xlsx$/i, "")}_UploadResponse`;
                    const headers = [
                      {
                        label: "SKU Code",
                        key: "skuCode",
                      },
                      {
                        label: "Message",
                        key: "message",
                      },
                    ]
                    downloadCsv(filename, file.uploadResponses.filter((x) => !x.success), headers)
                  }}
                >
                  <Icon>download</Icon>&nbsp;
                  export
                </MDButton>
              </MDTypography>
              <StripedDataGrid
                columns={[
                  {
                    headerName: "Status",
                    field: "success",
                    align: "center",
                    renderCell: (cell) =>
                      cell.row.success ? (
                        <StatusIcon color="success" icon="done" />
                      ) : (
                        <StatusIcon color="error" icon="close" />
                      ),
                  },
                  { headerName: "SKU Code", field: "skuCode", width: 200 },
                  {
                    headerName: "Message",
                    field: "message",
                    flex: 1,
                    wordWrapEnabled: true,
                    renderCell: (cell) => (
                      <div>
                        {(cell.row.message ?? []).map((c, i) => (
                          <p key={i}>{c}</p>
                        ))}
                      </div>
                    ),
                  },
                ]}
                rows={file.uploadResponses.map(x => {
                  return {
                    id: x.skuCode,
                    ...x
                  }
                })}
                getRowHeight={(cell) => {
                  if (cell.model?.message?.length >= 2) {
                    return "auto";
                  } else {
                    return 50;
                  }
                }}
              />
            </>
          )}
          {file.insufficientRows && file.insufficientRows.length > 0 && (() => {
            const rows = file.insufficientRows.filter(ir => ir.fileName === file.fileName)
            const exportRows = rows.map((ir, index) => {
              const { quantity_Decimal, skuCode } = ir;
              return {
                id: index,
                skuCode,
                ...quantity_Decimal
              }
            })
            const headerSet = [...new Set(rows.flatMap(ir => Object.keys(ir.quantity_Decimal)))];
            const header = [
              { label: 'SKU Code', key: 'skuCode' },
              ...headerSet.map(h => ({
                label: h,
                key: h
              }))
            ]
            const column = [
              { headerName: "SKU Code", field: "skuCode", width: 200 },
              ...headerSet.map(h => ({
                headerName: h,
                field: h,
                flex: 1
              }))
            ]
            return (
              <>
                <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
                  Remaining Quantity not Transferred{" "}
                  &nbsp;
                  <MDButton
                    type="submit" variant="gradient" color="info"
                    disabled={rows.length === 0}
                    onClick={() => {
                      const filename = file.fileName.replace(/\.xlsx$/i, ".csv");

                      downloadCsv(filename, exportRows, header)
                    }}
                  >
                    <Icon>download</Icon>&nbsp;
                    export remaining
                  </MDButton>
                </MDTypography>
                <StripedDataGrid
                  columns={column}
                  rows={exportRows}
                />
              </>
            )
          }
          )()}
        </InboundHeaderCard>
      ))}
      <InboundHeaderCard>
        <MDTypography sx={{ marginBottom: ".5rem" }} color="info" variant="h4">
          Submit Division Transfer
        </MDTypography>
        <Formik
          initialValues={{ reasonCode: reasonCode }}
          onSubmit={() => { }}
        >
          {({ setFieldValue }) => (
            <Form>
              <Grid item xs={6}>
                <TextField
                  field={{
                    type: "text",
                    name: "reasonCode",
                    label: "Reason",
                    required: true,
                    value: reasonCode,
                    onChange: (e) => {
                      setFieldValue("reasonCode", e.target.value)
                      setReasonCode(e.target.value)
                    }
                  }}
                />
              </Grid>
            </Form>
          )}
        </Formik>
        <InboundSubmitButton label="Submit" onClick={onSubmitInterDivision} disabled={!(fileResp.length) || !Boolean(reasonCode) || isInterDivisionSubmitting} />
      </InboundHeaderCard>

    </InboundLayout>
  );
}

const StatusIcon = ({ color, icon }) => (
  <MDButton color={color} variant="outlined" size="small" iconOnly circular>
    <Icon sx={{ fontWeight: "bold" }}>{icon}</Icon>
  </MDButton>
);

export default UploadDivisionTransfer;
